import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import {
  sendAbTastyClientPageview,
  syncPzHitsConfig,
  updatePzHitsCuid,
} from "~/lib/abtasty-client-hits.client";

/**
 * AB Tasty Universal Collect PAGEVIEW hits, client-side (P5 §5.3, D6) — the
 * replacement for the Worker's deleted server-side PAGEVIEW. Fires once on
 * first paint and once per SPA navigation (pathname/search change), which
 * gives soft navs the coverage the server hit — document requests only —
 * never had. `dr` carries `document.referrer` on the initial view and the
 * previous SPA location afterwards, mirroring how the vendor's own tag
 * reports SPA referrers.
 *
 * Also owns keeping `window.__PZ_HITS` in step with the root loader's
 * `pzHits` (PR #1050 review P1): the SSR inline script covers first paint,
 * but React never re-executes an updated inline script, so a SPA
 * login/logout would otherwise strand the global on the previous identity —
 * anonymous hits after login, or worse, the FORMER customer's cuid after
 * logout. The sync effect is declared before the pageview effect so a
 * config change lands before any hit that follows it in the same commit.
 *
 * Renders null. The sender itself is flag-gated server-side (no
 * `window.__PZ_HITS` ⇒ every call no-ops), so this component can mount
 * unconditionally outside the Builder editor; bots are excluded upstream
 * (the root loader never injects the sender config for bot requests).
 */
export function AbTastyPageviewTracker({
  config,
  sessionCuid,
}: {
  /** Root loader `pzHits` — null when the flag is off / bot / editor. */
  config: { cid: string; cuid?: string } | null;
  /** Root loader `hashedEmail` — the SESSION-computed sha256 lowercased
   * email (null for guests). Authoritative cuid for the sender. */
  sessionCuid: string | null;
}) {
  const location = useLocation();
  const previousHref = useRef<string | null>(null);

  const cid = config?.cid ?? null;
  const cuid = config?.cuid ?? null;
  useEffect(() => {
    syncPzHitsConfig(cid ? { cid, ...(cuid ? { cuid } : {}) } : null);
  }, [cid, cuid]);

  // Auth-transition audit V2 (PR #1084 review): the sender's cuid mirrors the
  // ROOT SESSION's hashed email, not Segment events. `pzHits.cuid` above is
  // ajs-cookie-derived and lags auth transitions (analytics.js writes
  // `ajs_user_id` ~1s AFTER the login revalidation already ran), and Segment
  // `identify` emits only after async dispatch — an in-flight identify
  // completing after a logout reset would resurrect the stale cuid.
  // `hashedEmail` lands with the same revalidation that swaps the rest of the
  // chrome (root/layout/catch-all all revalidate on auth submits). Declared
  // after — and keyed on — the config sync so it re-asserts the session
  // identity over every synced (possibly stale) loader cuid, including the
  // first-paint SSR value. No-ops while the sender is off.
  useEffect(() => {
    updatePzHitsCuid(sessionCuid);
  }, [sessionCuid, cid, cuid]);

  // pathname + search define a "page view"; hash-only changes don't.
  //
  // COM-585: this tracker deliberately KEEPS its own trigger rather than
  // following the site-owned navigation rule (martech, 2026-09-26). Moving it
  // to pathname-only would drop a PAGEVIEW hit on every PLP filter
  // interaction — a reduction in AB Tasty volume, and the conservative call
  // is no vendor volume change at all. C5 still holds: no INCREASE per action.
  const pageKey = `${location.pathname}${location.search}`;

  useEffect(() => {
    const dl = window.location.href;
    sendAbTastyClientPageview({
      dl,
      dr: previousHref.current ?? document.referrer ?? undefined,
    });
    previousHref.current = dl;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pageKey IS the
    // location dependency; `location` itself changes identity on every
    // navigation including hash/state-only ones this deliberately skips.
  }, [pageKey]);

  return null;
}
